import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router';

import { useAuth } from '@/features/auth/contexts/auth.context';
import { useInterviewContext } from '@/features/common/contexts/interview.context';
import { useUserContext } from '@/features/common/contexts/user.context';
import { useBooth } from '@/features/settings/hooks/useBooth';

interface HomeGuardProps {
  children: React.ReactNode;
}

/**
 * Wraps the home page.
 *
 * If interviewSource is Dashboard in context but not in URL (e.g. after reset to home from
 * dashboard flow), redirect to /dashboard (candidate app). No isBooth check on that path.
 *
 * On booth/kiosk only: if still authenticated on `/`, logout to avoid session reuse between
 * visitors. Candidate dashboard is not used on booth devices, so we do not special-case
 * cameFromDashboard for this logout (non-booth never hits this branch).
 */
export function HomeGuard({ children }: HomeGuardProps) {
  const { cameFromDashboard, comesFromDashboard } = useInterviewContext();
  const { isAuthenticated } = useAuth();
  const { logout } = useUserContext();
  const { isBooth, loading: boothLoading } = useBooth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    (async () => {
      if (!isAuthenticated) {
        return;
      }
      if (isLoggingOut) {
        return;
      }
      if (boothLoading || !isBooth) {
        return;
      }

      try {
        setIsLoggingOut(true);
        await logout();
      } catch (error) {
        console.error('Failed to logout', error);
      } finally {
        setIsLoggingOut(false);
      }
      return;
    })();
  }, [isAuthenticated, logout, isLoggingOut, isBooth, boothLoading]);

  const dashboardReset = useMemo(
    () => cameFromDashboard && !comesFromDashboard,
    [cameFromDashboard, comesFromDashboard],
  );

  // After reset to home from dashboard: redirect to dashboard (no isBooth/session check)
  if (dashboardReset) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
